import { useResource } from "hooks/useResource";
import { getTeachers } from "services/content";
import { mediaUrl } from "lib/media";

/**
 * Teaching staff for one institution.
 *
 * The school collects a fixed set of details per teacher — name, designation,
 * highest qualification, mobile number, which institution, and a recent
 * photograph — so the card shows exactly those and invents nothing.
 *
 * Scoped by the `institution` slug rather than the site's usual `tags` array:
 * a teacher belongs to one of the three, which is how the school's own form
 * asks for it. An institution with nobody listed renders nothing at all.
 */

/** Fallback when there is no photograph yet: the edges of the name. */
function initialsOf(name = "") {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  // Bangla has no case, so this is simply the leading glyph of the first and
  // last word — enough to fill the circle without looking like a mistake.
  const first = [...words[0]][0] || "";
  const last = words.length > 1 ? [...words[words.length - 1]][0] || "" : "";
  return first + last;
}

function TeacherCard({ teacher, color }) {
  const photo = teacher.photo?.startsWith("/uploads/")
    ? mediaUrl(teacher.photo)
    : teacher.photo;

  return (
    <article className="flex gap-4 rounded-2xl border border-line bg-white p-4 sm:p-5">
      <div
        className="w-[76px] h-[92px] shrink-0 rounded-xl overflow-hidden flex items-center justify-center"
        style={{ background: `${color}14` }}
      >
        {photo ? (
          <img
            src={photo}
            alt={teacher.name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="font-bn text-[20px] font-bold" style={{ color }} aria-hidden="true">
            {initialsOf(teacher.name)}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="font-bn text-[17px] font-bold text-ink leading-snug">{teacher.name}</h3>
        {teacher.designation && (
          <p className="font-bn text-[14px] font-semibold mt-0.5" style={{ color }}>
            {teacher.designation}
          </p>
        )}
        {teacher.qualification && (
          <p className="font-bn text-[13.5px] text-muted mt-1 leading-snug">
            {teacher.qualification}
          </p>
        )}
        {teacher.phone && (
          <a
            href={`tel:${teacher.phone.replace(/\s/g, "")}`}
            className="inline-block text-[13.5px] text-green mt-2 no-underline hover:underline"
          >
            {teacher.phone}
          </a>
        )}
      </div>
    </article>
  );
}

export default function TeacherGrid({ institution, color = "#005C40" }) {
  const { data } = useResource(getTeachers);

  const teachers = (data?.items || []).filter(
    (t) => t?.institution === institution && (t.name || "").trim()
  );
  if (!teachers.length) return null;

  return (
    <section className="max-w-container mx-auto px-4 sm:px-10 pb-16">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] mb-1.5" style={{ color }}>
          Faculty
        </p>
        <h2 className="font-serif text-[26px] sm:text-[32px] font-bold text-ink leading-tight">
          <span className="font-bn">শিক্ষকমণ্ডলী</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {teachers.map((t, i) => (
          <TeacherCard key={`${t.name}-${i}`} teacher={t} color={color} />
        ))}
      </div>
    </section>
  );
}
